import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded'
import { Box, IconButton, Tooltip } from '@mui/material'

import { GRTable, GRTableColumn } from '../../../../components/GRTable/GRTable'
import { creativeTagListingColumns } from '../../helpers/tagColumns'
import { useControlledColumns } from '../../hooks/useControlledColumns'
import { useGenreTagsListing } from '../../hooks/useGenreTagsListing'
import { useUAInsightsMeta } from '../../hooks/useUAInsightsMeta'
import { useUAInsightsNavigation } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { CreativeTag } from '../../types'
import { CreativeAdsTable } from '../CreativeAdsTable/CreativeAdsTable'
import { ListingTruncationNote } from '../ListingTruncationNote/ListingTruncationNote'
import { SectionCard } from '../SectionCard/SectionCard'
import { TableSkeleton } from '../TableSkeleton/TableSkeleton'
import { UAInsightsDialog } from '../UAInsightsDialog/UAInsightsDialog'

const COLLAPSED_TAG_COUNT = 5

const tagRowId = (row: CreativeTag) => row.tag.id

export const GenreTagsListingDialog: FC = () => {
  const { t } = useTranslation('ua-insights')
  const { params, close, canGoBack, goBack, openTag, openMotivation, openTagGames, openGenreTags } = useUAInsightsNavigation()
  const taxonomy = useUATaxonomy()
  const { genreById } = useUAInsightsMeta()
  const open = params.dialog === 'genre-tags'
  const genreId = open ? params.genreId : null
  const { data, isLoading, isError, refetch } = useGenreTagsListing(genreId)
  const [expanded, setExpanded] = useState(false)

  // Reset the expanded state whenever the dialog closes or its target changes
  // so re-opening via back navigation always starts collapsed.
  useEffect(() => {
    if (!open) setExpanded(false)
  }, [open, genreId])

  const defaultTagColumns: GRTableColumn<CreativeTag, undefined>[] = useMemo(
    () =>
      creativeTagListingColumns(t, taxonomy, {
        onTagClick: openTag,
        onTagGamesClick: openTagGames,
        onGenreClick: openGenreTags,
        onMotivationClick: openMotivation,
      }),
    [t, taxonomy, openTag, openTagGames, openGenreTags, openMotivation]
  )

  const [tagColumns, setTagColumns] = useControlledColumns(defaultTagColumns)

  const genre = genreId ? genreById(genreId) : undefined
  const title = genre ? t('creative_tags_for', { genre: taxonomy.genreLabel(genre) }) : ''

  const prevalentTags = data?.prevalentTags ?? []
  const canExpand = prevalentTags.length > COLLAPSED_TAG_COUNT
  const toggleLabel = expanded ? t('common:show_less') : t('common:show_more')

  return (
    <UAInsightsDialog
      open={open}
      onClose={close}
      title={title}
      canGoBack={canGoBack}
      onGoBack={goBack}
      showNetworkSelector
      hasData={!!data}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      renderChildrenWhenEmpty
      loadingPlaceholder={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TableSkeleton rows={6} columns={5} />
          <TableSkeleton rows={6} columns={6} />
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <SectionCard
          title={t('most_prevalent_tags')}
          disableBodyPadding
          headerAction={
            canExpand ? (
              <Tooltip title={toggleLabel}>
                <IconButton size="small" onClick={() => setExpanded((prev) => !prev)} aria-label={toggleLabel}>
                  {expanded ? <ExpandLessRounded /> : <ExpandMoreRounded />}
                </IconButton>
              </Tooltip>
            ) : undefined
          }
        >
          <GRTable
            rows={prevalentTags}
            columns={tagColumns}
            onColumnsUpdated={setTagColumns}
            maxRows={expanded ? undefined : COLLAPSED_TAG_COUNT}
            striped
            hoverable
            rowIdKey={tagRowId}
            noRowsLabel={t('no_tags')}
          />
        </SectionCard>

        <CreativeAdsTable rows={data?.creativeAds ?? []} />
        <ListingTruncationNote shown={data?.creativeAds.length ?? 0} total={data?.creativesTotal} />
      </Box>
    </UAInsightsDialog>
  )
}
